import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PROGRAMS = [
  "Portfolio Kickstart",
  "Stock Picks Mentorship",
  "Long-Term Wealth Plan",
  "Not sure yet",
];

const TIME_SLOTS = [
  "08:00",
  "09:30",
  "11:00",
  "13:00",
  "14:30",
  "16:00",
  "17:30",
];

const TIMEZONES = [
  "Europe/London",
  "Europe/Rome",
  "Europe/Berlin",
  "America/New_York",
  "America/Los_Angeles",
  "Asia/Singapore",
  "Asia/Dubai",
];

export const Booking = () => {
  const today = new Date();
  const [date, setDate] = useState(undefined);
  const [time, setTime] = useState("");
  const [tz, setTz] = useState("Europe/Rome");
  const [program, setProgram] = useState("Stock Picks Mentorship");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required.");
      return;
    }
    if (!date || !time) {
      toast.error("Please choose a date and time.");
      return;
    }
    setLoading(true);
    try {
      const isoDate = date.toISOString().slice(0, 10);
      await api.post("/bookings", {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        program,
        preferred_date: isoDate,
        preferred_time: time,
        timezone: tz,
        notes: form.notes || null,
      });
      toast.success("Booking received — Leo will reach out within 24h.");
      setForm({ name: "", email: "", phone: "", notes: "" });
      setDate(undefined);
      setTime("");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="relative py-32 lg:py-40 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-radial-amber opacity-60 -z-10" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Left: copy + calendar */}
          <div className="lg:col-span-6">
            <p className="text-xs uppercase tracking-[0.35em] text-amber-500 mb-6">
              — Book a call
            </p>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight">
              Let's get your money <em className="italic text-amber-400">moving.</em>
            </h2>
            <p className="mt-6 text-zinc-400 text-lg max-w-md">
              Book a free 30-minute call. We'll talk about where you are, where
              you want your portfolio to go, and which service makes sense for
              you. No pressure, no pitch deck.
            </p>

            <div className="mt-12 rounded-3xl border border-white/10 bg-[#0A0A0A] p-6">
              <Label className="text-xs uppercase tracking-[0.3em] text-amber-500">
                Pick a date
              </Label>
              <div className="mt-4 flex justify-center">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d < new Date(today.toDateString())}
                  data-testid="booking-calendar"
                  className="text-white"
                />
              </div>

              <div className="mt-6">
                <Label className="text-xs uppercase tracking-[0.3em] text-amber-500">
                  Time slot
                </Label>
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {TIME_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      data-testid={`time-slot-${slot}`}
                      onClick={() => setTime(slot)}
                      className={`text-sm py-2.5 rounded-full border transition-all ${
                        time === slot
                          ? "bg-amber-500 text-black border-amber-500"
                          : "border-white/15 text-zinc-300 hover:border-white/40"
                      }`}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <Label className="text-xs uppercase tracking-[0.3em] text-amber-500">
                  Timezone
                </Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="timezone-trigger"
                      className="mt-3 w-full flex items-center justify-between bg-black/40 border border-white/15 hover:border-white/30 text-white rounded-xl px-4 py-3 text-sm"
                    >
                      <span>{tz}</span>
                      <span className="text-amber-500">▾</span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="bg-[#0A0A0A] border-white/10 text-white"
                  >
                    <DropdownMenuLabel className="text-zinc-400 text-xs uppercase tracking-widest">
                      Select your zone
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {TIMEZONES.map((z) => (
                      <DropdownMenuItem
                        key={z}
                        data-testid={`timezone-option-${z}`}
                        onSelect={() => setTz(z)}
                        className="focus:bg-amber-500/10 focus:text-amber-300"
                      >
                        {z}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="lg:col-span-6 rounded-3xl border border-white/10 bg-[#0A0A0A] p-8 lg:p-10 space-y-6"
          >
            <div>
              <Label htmlFor="name" className="text-zinc-300">
                Full name
              </Label>
              <Input
                id="name"
                required
                data-testid="form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-2 bg-black/40 border-white/15 text-white placeholder:text-zinc-600 h-12 rounded-xl"
                placeholder="Marco Rossi"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email" className="text-zinc-300">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  data-testid="form-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 bg-black/40 border-white/15 text-white placeholder:text-zinc-600 h-12 rounded-xl"
                  placeholder="you@email.com"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-zinc-300">
                  Phone (optional)
                </Label>
                <Input
                  id="phone"
                  data-testid="form-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-2 bg-black/40 border-white/15 text-white placeholder:text-zinc-600 h-12 rounded-xl"
                  placeholder="+39 ..."
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-300">Program of interest</Label>
              <Select value={program} onValueChange={setProgram}>
                <SelectTrigger
                  data-testid="form-program"
                  className="mt-2 bg-black/40 border-white/15 text-white h-12 rounded-xl"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10 text-white">
                  {PROGRAMS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="notes" className="text-zinc-300">
                Tell me about your trading
              </Label>
              <Textarea
                id="notes"
                rows={5}
                data-testid="form-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-2 bg-black/40 border-white/15 text-white placeholder:text-zinc-600 rounded-xl"
                placeholder="How long have you been trading? What's not working?"
              />
            </div>

            <div className="flex items-center justify-between gap-4 pt-2">
              <div className="text-xs text-zinc-500">
                {date ? (
                  <span data-testid="selected-summary">
                    {date.toDateString()} · {time || "select a slot"} · {tz}
                  </span>
                ) : (
                  <span>Pick a date & time to confirm your slot.</span>
                )}
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="booking-submit"
                className="bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-full px-8 py-6"
              >
                {loading ? "Sending..." : "Reserve my call →"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
